import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import { IconX } from "@tabler/icons-react";
import type * as React from "react";

import { cn } from "../lib/utils";
import { buttonVariants } from "./button";

/**
 * Short notices at the bottom of the screen ("Variant verwijderd · Ongedaan maken"), on Base UI's
 * toast. `ToastProvider` wraps the app once and renders the stack; a component adds one with
 * `useToast().add({ title, actionProps: { children, onClick } })`. `closeLabel` names the close
 * button for screen readers (pass it through i18n; a primitive holds no text of its own).
 */
function ToastProvider({
	children,
	closeLabel,
	...props
}: ToastPrimitive.Provider.Props & { closeLabel?: string }) {
	return (
		<ToastPrimitive.Provider {...props}>
			{children}
			<ToastPrimitive.Portal>
				<ToastPrimitive.Viewport
					data-slot="toast-viewport"
					className="fixed right-4 bottom-4 z-[60] mx-auto w-[calc(100vw-2rem)] sm:right-8 sm:bottom-8 sm:w-[24rem]"
				>
					<ToastList closeLabel={closeLabel} />
				</ToastPrimitive.Viewport>
			</ToastPrimitive.Portal>
		</ToastPrimitive.Provider>
	);
}

function ToastList({ closeLabel }: { closeLabel?: string }) {
	const { toasts } = ToastPrimitive.useToastManager();
	return toasts.map((toast) => (
		<ToastPrimitive.Root
			key={toast.id}
			toast={toast}
			data-slot="toast"
			className={cn(
				"[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]",
				"absolute right-0 bottom-0 left-auto z-[calc(1000-var(--toast-index))] h-(--height) w-full origin-bottom rounded-2xl bg-popover bg-clip-padding text-popover-foreground shadow-lg ring-1 ring-foreground/10 select-none",
				"[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]",
				"after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
				"data-ending-style:opacity-0 data-expanded:h-(--toast-height) data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))] data-limited:opacity-0 data-starting-style:[transform:translateY(150%)] [&[data-ending-style]:not([data-limited])]:[transform:translateY(150%)]",
			)}
		>
			<ToastPrimitive.Content className="flex items-center gap-3 overflow-hidden px-4 py-3 text-sm transition-opacity duration-250 data-behind:opacity-0 data-expanded:opacity-100">
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<ToastPrimitive.Title className="font-medium" />
					<ToastPrimitive.Description className="text-muted-foreground" />
				</div>
				<ToastPrimitive.Action className={buttonVariants({ variant: "outline", size: "sm" })} />
				<ToastPrimitive.Close
					aria-label={closeLabel}
					className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
				>
					<IconX aria-hidden />
				</ToastPrimitive.Close>
			</ToastPrimitive.Content>
		</ToastPrimitive.Root>
	));
}

/** Adds, updates and closes toasts; inside `ToastProvider`. */
const useToast = ToastPrimitive.useToastManager;

export type ToastOptions = Parameters<ReturnType<typeof useToast>["add"]>[0];
export type ToastProviderProps = React.ComponentProps<typeof ToastProvider>;

export { ToastProvider, useToast };
